'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Heart, User, LogOut, BookOpen, ChevronDown, LayoutDashboard } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const navLinks = [
  { label: 'Find Venues', href: '/venue-listing-page' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'List Your Venue', href: '#list-venue' },
  { label: 'About Us', href: '#about' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, loading, signOut } = useAuth();
  const router = useRouter();

  useEffect(() => {
    setMounted(true);
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isScrolled = mounted && scrolled;

  const handleSignOut = async () => {
    try {
      await signOut();
      setUserMenuOpen(false);
      router?.push('/home-page');
      router?.refresh();
    } catch (err) {
      // handle silently
    }
  };

  const getUserInitials = () => {
    const name = user?.user_metadata?.full_name || user?.email || '';
    if (!name) return 'U';
    const parts = name?.split(' ');
    if (parts?.length >= 2) return `${parts?.[0]?.[0]}${parts?.[1]?.[0]}`?.toUpperCase();
    return name?.slice(0, 2)?.toUpperCase();
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link href="/home-page" className="flex items-center gap-2">
          <AppLogo size={36} />
          <span className={`font-extrabold text-xl tracking-tight transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
            Festaa
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks?.map((link) => (
            <Link
              key={`nav-${link?.label}`}
              href={link?.href}
              className={`text-sm font-semibold transition-colors hover:text-red-600 ${
                isScrolled ? 'text-gray-700' : 'text-white/90'
              }`}
            >
              {link?.label}
            </Link>
          ))}
        </nav>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-3">
          <button
            className={`p-2 rounded-full transition-colors hover:bg-red-50 hover:text-red-600 ${
              isScrolled ? 'text-gray-600' : 'text-white hover:bg-white/20'
            }`}
            aria-label="Wishlist"
          >
            <Heart size={20} />
          </button>

          {!loading && (
            <>
              {user ? (
                /* Authenticated user menu */
                (<div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
                      isScrolled
                        ? 'text-gray-700 hover:bg-gray-100' :'text-white hover:bg-white/20'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center text-xs font-bold">
                      {getUserInitials()}
                    </div>
                    <span className="text-sm font-semibold max-w-[100px] truncate">
                      {user?.user_metadata?.full_name?.split(' ')?.[0] || 'Account'}
                    </span>
                    <ChevronDown size={14} />
                  </button>
                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-100 mb-1">
                        <p className="text-xs text-gray-500">Signed in as</p>
                        <p className="text-sm font-semibold text-gray-800 truncate">{user?.email}</p>
                      </div>
                      <button
                        onClick={() => { setUserMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <Heart size={15} /> My Wishlist
                      </button>
                      <button
                        onClick={() => { setUserMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <BookOpen size={15} /> Booking History
                      </button>
                      <button
                        onClick={() => { setUserMenuOpen(false); router?.push('/venue-owner-dashboard'); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <LayoutDashboard size={15} /> Venue Dashboard
                      </button>
                      <button
                        onClick={() => { setUserMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                      >
                        <User size={15} /> My Profile
                      </button>
                      <hr className="my-1 border-gray-100" />
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut size={15} /> Sign Out
                      </button>
                    </div>
                  )}
                </div>)
              ) : (
                /* Guest buttons */
                (<>
                  <Link
                    href="/login"
                    className={`text-sm font-semibold px-4 py-2 rounded-xl transition-all ${
                      isScrolled
                        ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/20'
                    }`}
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/login"
                    className="btn-primary text-sm px-5 py-2.5 rounded-xl"
                  >
                    Get Started
                  </Link>
                </>)
              )}
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden p-2 rounded-lg transition-colors ${
            isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/20'
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {/* Mobile Drawer */}
      <div
        className={`md:hidden bg-white border-t border-gray-100 transition-all duration-300 overflow-hidden ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {navLinks?.map((link) => (
            <Link
              key={`mobile-nav-${link?.label}`}
              href={link?.href}
              onClick={() => setMobileOpen(false)}
              className="text-gray-700 font-semibold py-3 px-3 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
            >
              {link?.label}
            </Link>
          ))}
          <hr className="my-2 border-gray-100" />

          {!loading && (
            <>
              {user ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-3 px-3 py-2 mb-2">
                    <div className="w-9 h-9 rounded-full bg-red-600 text-white flex items-center justify-center text-sm font-bold">
                      {getUserInitials()}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {user?.user_metadata?.full_name || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                  <button className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors">
                    <Heart size={15} /> My Wishlist
                  </button>
                  <button className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors">
                    <BookOpen size={15} /> Booking History
                  </button>
                  <button
                    onClick={() => { setMobileOpen(false); router?.push('/venue-owner-dashboard'); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-gray-700 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    <LayoutDashboard size={15} /> Venue Dashboard
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={15} /> Sign Out
                  </button>
                </div>
              ) : (
                <div className="flex gap-3 pt-1">
                  <Link
                    href="/login"
                    onClick={() => setMobileOpen(false)}
                    className="flex-1 text-center border-2 border-red-600 text-red-600 font-semibold py-2.5 rounded-xl hover:bg-red-600 hover:text-white transition-all"
                  >
                    Sign In
                  </Link>
                  <Link
                    href="/login"
                    onClick={() => setMobileOpen(false)}
                    className="flex-1 text-center bg-red-600 text-white font-semibold py-2.5 rounded-xl hover:bg-red-700 transition-all"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}